import {
	IconArrowsMaximize,
	IconArrowsMinimize,
	IconTrash,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import {
	Alert,
	Button,
	Descriptions,
	Flex,
	Segmented,
	Space,
	Tag,
	Tooltip,
	Typography,
	theme,
} from "antd";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import {
	useAnalyzeTerraform,
	useDeleteDeployment,
	useRefreshDeployment,
} from "@/common/artemis-operations/deployment";
import { useEnvironments } from "@/common/artemis-operations/environments";
import {
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	DEPLOYMENT_GRAPH_DISCOVERING_DESC,
	DEPLOYMENT_GRAPH_DISCOVERING_ERROR_DESC,
	DEPLOYMENT_GRAPH_DISCOVERING_ERROR_TITLE,
	ERROR_NETWORK_DESC,
	ERROR_NETWORK_TITLE,
} from "@/common/messages";
import type { Deployment, Signal, SignalStatus } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import { toLabel } from "@/common/utils";
import { BaseContainer, CustomIcon } from "../base";
import { DeploymentGraphFlow } from "../deployment-graph-flow";
import { useFrictionConfirm } from "../friction-confirm-dialog";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { SignalRows } from "../signals/signal-list";
import { GraphTypeBadge } from "./project-badges";

export function DeploymentViewerDrawer({
	open,
	onClose,
	onDeleted,
	deployment,
	projectId,
	workspaceId,
	signals,
	signalStatuses,
	signalStatusesFetching,
	onOpenSignal,
	onAddSignal,
}: {
	deployment: Deployment | null;
	open: boolean;
	onClose: () => void;
	/** Called after the deployment is deleted, so the parent can refresh the project. */
	onDeleted?: () => void;
	projectId: string;
	workspaceId: string;
	/** This deployment's signals, from the project's signal list. */
	signals: Signal[];
	signalStatuses?: Record<string, SignalStatus>;
	/** True while the project is re-reading signal statuses. */
	signalStatusesFetching?: boolean;
	onOpenSignal: (signal: Signal) => void;
	onAddSignal: (deploymentId: string) => void;
}) {
	const notify = useNotify();
	const queryClient = useQueryClient();
	const { token } = theme.useToken();
	const fsRef = useRef<HTMLDivElement>(null);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const { confirm, dialog } = useFrictionConfirm();
	const [content, setContent] = useState<string>("graph");
	const {
		refresh: refreshDeployment,
		error,
		isPending,
	} = useRefreshDeployment(deployment?.id ?? "");
	const { analyzeTerraform, isPending: isAnalyzing } = useAnalyzeTerraform(
		projectId,
		deployment?.workspaceId ?? "",
	);
	const terraformFileInput = useRef<HTMLInputElement>(null);
	const { deleteDeployment, isPending: isDeleting } =
		useDeleteDeployment(projectId);
	const { data: environments } = useEnvironments(workspaceId);
	const environment = environments?.find(
		(e) => e?.id === deployment?.environmentId,
	);

	// Discovery runs in the background on the server; poll the project so the
	// deployment's status updates here until the run finishes.
	const isDiscovering =
		isPending || isAnalyzing || !!deployment?.graph?.isDiscovering;
	// A Terraform graph was built from files that were deleted once read, so there is nothing to
	// refresh from — rebuilding it means supplying the files again.
	const isTerraform = !!deployment?.terraformConfig;
	useEffect(() => {
		if (!open || !deployment?.graph?.isDiscovering) return;
		const timer = setInterval(() => {
			queryClient.invalidateQueries({ queryKey: ["project", projectId] });
		}, 4000);
		return () => clearInterval(timer);
	}, [open, deployment?.graph?.isDiscovering, projectId, queryClient]);

	useEffect(() => {
		if (error)
			notify.error(ERROR_NETWORK_TITLE, ERROR_NETWORK_DESC || error.message);
	}, [error, notify]);

	// Track native fullscreen so we can render the in-fullscreen toolbar (Esc also exits).
	useEffect(() => {
		const handler = () =>
			setIsFullscreen(document.fullscreenElement === fsRef.current);
		document.addEventListener("fullscreenchange", handler);
		return () => document.removeEventListener("fullscreenchange", handler);
	}, []);

	if (!deployment) return null;

	const startDiscovery = () =>
		refreshDeployment(deployment.id, {
			onSuccess: () =>
				queryClient.invalidateQueries({ queryKey: ["project", projectId] }),
		});

	const handleTerraformFiles = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files ?? []);
		// Reset the input so picking the same files again still fires a change event.
		event.target.value = "";
		if (files.length === 0) return;

		analyzeTerraform({ deploymentId: deployment.id, files }).catch(
			(e: unknown) =>
				notify.error(
					ERROR_NETWORK_TITLE,
					e instanceof Error ? e.message : ERROR_NETWORK_DESC,
				),
		);
	};

	const signalsLabel =
		signals.length > 0 ? `Signals (${signals.length})` : "Signals";

	const toggleFullscreen = () => {
		if (document.fullscreenElement) void document.exitFullscreen();
		else void fsRef.current?.requestFullscreen();
	};

	const handleDelete = () => {
		confirm({
			title: DELETE_RESOURCE_TITLE,
			message: DELETE_RESOURCE_DESC,
			onConfirm: () => {
				deleteDeployment(deployment.id, {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						onClose();
						onDeleted?.();
					},
					onError: () => notify.error(DELETE_RESOURCE_ERROR_FAILED),
				});
			},
		});
	};

	return (
		<CustomDrawer
			title={
				<Typography.Text
					ellipsis={{ tooltip: deployment.name }}
					style={{ maxWidth: "100%" }}
				>
					{deployment.name}
				</Typography.Text>
			}
			open={open}
			onClose={onClose}
			size="large"
			loadingMessage={
				content === "graph" && isDiscovering
					? DEPLOYMENT_GRAPH_DISCOVERING_DESC
					: undefined
			}
			extra={
				<Space>
					<Segmented
						options={[
							{
								value: "graph",
								label: "Graph",
							},
							{
								value: "details",
								label: "Details",
							},
							{
								value: "signals",
								label: signalsLabel,
							},
						]}
						value={content}
						onChange={(value) => setContent(value)}
					/>
					<Tooltip title="Fullscreen architecture">
						<Button
							type="text"
							icon={<CustomIcon icon={IconArrowsMaximize} size={18} />}
							onClick={toggleFullscreen}
						/>
					</Tooltip>
					<Tooltip title="Delete deployment">
						<Button
							type="text"
							danger
							disabled={isDeleting}
							icon={<CustomIcon icon={IconTrash} size={18} />}
							onClick={handleDelete}
						/>
					</Tooltip>
					{isTerraform && (
						<input
							ref={terraformFileInput}
							type="file"
							multiple
							hidden
							accept=".tfstate,.tf,.json,.backup"
							onChange={handleTerraformFiles}
						/>
					)}
					<Tooltip
						title={
							isTerraform
								? "Supply the Terraform files again to rebuild the graph"
								: undefined
						}
					>
						<Button
							type="primary"
							disabled={isDiscovering}
							onClick={
								isTerraform
									? () => terraformFileInput.current?.click()
									: startDiscovery
							}
						>
							{isTerraform ? "Re-analyze" : "Discover"}
						</Button>
					</Tooltip>
				</Space>
			}
		>
			<div
				ref={fsRef}
				style={{
					height: "100%",
					display: "flex",
					flexDirection: "column",
					...(isFullscreen
						? { background: token.colorBgContainer, padding: 12 }
						: {}),
				}}
			>
				{isFullscreen && (
					<Flex
						align="center"
						justify="space-between"
						style={{ marginBottom: 12 }}
					>
						<Segmented
							options={[
								{ value: "graph", label: "Graph" },
								{ value: "details", label: "Details" },
								{ value: "signals", label: signalsLabel },
							]}
							value={content}
							onChange={(value) => setContent(value)}
						/>
						<Tooltip title="Exit fullscreen">
							<Button
								type="text"
								icon={<CustomIcon icon={IconArrowsMinimize} size={18} />}
								onClick={toggleFullscreen}
							/>
						</Tooltip>
					</Flex>
				)}
				<div
					style={{
						flex: "1 1 auto",
						minHeight: 0,
						overflow: content === "graph" ? undefined : "auto",
					}}
				>
					{content === "graph" &&
						!isDiscovering &&
						deployment.graph?.discoveryError && (
							<Alert
								type="error"
								title={DEPLOYMENT_GRAPH_DISCOVERING_ERROR_TITLE}
								description={DEPLOYMENT_GRAPH_DISCOVERING_ERROR_DESC}
							/>
						)}
					{content === "graph" &&
						!isDiscovering &&
						!deployment.graph?.discoveryError && (
							<DeploymentGraphFlow graph={deployment.graph} />
						)}
					{content === "signals" && (
						<BaseContainer
							size="small"
							title="Signals"
							actions={
								<Button size="small" onClick={() => onAddSignal(deployment.id)}>
									Add signal
								</Button>
							}
						>
							<SignalRows
								signals={signals}
								statuses={signalStatuses}
								isFetching={signalStatusesFetching}
								onOpen={onOpenSignal}
							/>
						</BaseContainer>
					)}
					{content === "details" && (
						<Space vertical style={{ width: "100%" }}>
							<Descriptions
								column={1}
								size="default"
								items={[
									{
										key: "description",
										label: "Description",
										children: toLabel(deployment.description),
									},
									{
										key: "environmentId",
										label: "Environment",
										children: environment?.name ?? deployment.environmentId,
									},
									{
										key: "createdAt",
										label: "Created",
										children: format(
											new Date(deployment.createdAt as string),
											"MM/dd/yyyy h:mm a",
										),
									},
									...(deployment.config
										? [
												{
													key: "region",
													label: "Region",
													children: deployment.config.region ?? "Default",
												},
												{
													key: "stackNames",
													label: "Stacks",
													children: (
														<Space wrap>
															{deployment.config.stackNames.map((s) => (
																<Tag key={s}>{s}</Tag>
															))}
														</Space>
													),
												},
											]
										: []),
									// Only once an analysis has actually produced files; the config exists
									// from creation, with SourceNames empty until then.
									...(deployment.terraformConfig?.sourceNames.length
										? [
												{
													key: "terraformSources",
													label: "Built from",
													children: (
														<Space size={[4, 4]} wrap>
															{deployment.terraformConfig.sourceNames.map(
																(name) => (
																	<Tag key={name}>{name}</Tag>
																),
															)}
														</Space>
													),
												},
											]
										: []),
									...(deployment.graph
										? [
												{
													key: "graphType",
													label: "Graph Type",
													children: (
														<GraphTypeBadge type={deployment.graph.type} />
													),
												},
												...(deployment.graph.lastDiscoveryAt
													? [
															{
																key: "lastDiscoveryAt",
																label: "Last discovered",
																children: format(
																	new Date(
																		deployment.graph.lastDiscoveryAt as string,
																	),
																	"MM/dd/yyyy h:mm a",
																),
															},
														]
													: []),
												...(deployment.graph.discoveryError
													? [
															{
																key: "discoveryError",
																label: "Discovery Error",
																children: deployment.graph.discoveryError,
															},
														]
													: []),
											]
										: []),
								]}
							/>
						</Space>
					)}
				</div>
			</div>
			{dialog}
		</CustomDrawer>
	);
}
